import {
  Button,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FieldWrapper,
  Label,
  RadioGroup,
  RadioGroupItem,
  SingleCombobox,
} from '@commenda/kakashi';
import { DirectorBase } from '@commenda/types/src/incorporation/types';
import { useEffect, useState } from 'react';
import { useNotification } from '../../../../hooks/useNotification';
import { getDirectorName } from '../../Director/common';
import { AuthorizedSignatoryMode } from './types';

const AuthorizedSignatoryDetails = ({
  existingAuthorizedSignatory,
  onBack,
  onNext,
  companyId,
  directors,
  onSelectDirector,
  onModeChange,
}: {
  existingAuthorizedSignatory: DirectorBase;
  onBack: () => void;
  onNext: () => void;
  companyId: number;
  directors: DirectorBase[];
  onSelectDirector: (directorId: number) => void;
  onModeChange: (mode: AuthorizedSignatoryMode) => void;
}) => {
  const { setNotification } = useNotification();

  // State for managing the mode
  const [mode, setMode] = useState<AuthorizedSignatoryMode>('new');
  const selectedDirectorId = existingAuthorizedSignatory?.id;

  useEffect(() => {
    if (existingAuthorizedSignatory) {
      setMode('existing');
    } else {
      setMode('new');
    }
  }, [existingAuthorizedSignatory]);

  const onSubmit = async () => {
    if (mode === 'existing') {
      const selectedDirector = directors?.find(
        (dir) => dir.id === selectedDirectorId
      );

      if (!selectedDirector) {
        setNotification({
          show: true,
          valid: false,
          title: 'Error',
          message: 'Selected director not found.',
        });
        return;
      }
    }

    onNext();
  };

  return (
    <div>
      <DialogHeader className="border-none">
        <DialogTitle>Authorized Signatory Details</DialogTitle>
      </DialogHeader>
      <div className="overflow-y-auto flex flex-col">
        <div className="flex flex-col gap-5 grow overflow-y-auto px-6">
          {/* Mode Selection */}
          <FieldWrapper _className="gap-5">
            <Label>
              How would you like to assign the authorized signatory?
            </Label>
            <RadioGroup
              value={mode}
              onValueChange={(value: AuthorizedSignatoryMode) => {
                setMode(value);
                onModeChange(value);
              }}
              className="flex flex-col gap-3"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="new" id="new" />
                <Label htmlFor="new">Add new person</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="existing" id="existing" />
                <Label htmlFor="existing">Select existing director</Label>
              </div>
            </RadioGroup>
          </FieldWrapper>

          {/* Conditional Content Based on Mode */}
          {mode === 'existing' ? (
            <FieldWrapper>
              <Label>
                <span>Select Director</span>
                <span className="text-utility-violet-600">*</span>
              </Label>
              <SingleCombobox
                key={`director-select-${mode}`}
                options={
                  directors?.map((eachDirector) => ({
                    label: `${getDirectorName(eachDirector)}${
                      eachDirector?.email ? ` (${eachDirector.email})` : ''
                    }`,
                    value: eachDirector.id.toString(),
                  })) || []
                }
                value={selectedDirectorId?.toString() || ''}
                setValue={(value) => {
                  onSelectDirector(+value);
                }}
                placeholder="Select a director"
                disabled={!directors?.length}
                canClearSelection={false}
              />
            </FieldWrapper>
          ) : null}
        </div>
        <DialogFooter className="border-none">
          <Button size="sm" variant="secondary" type="button" onClick={onBack}>
            Back
          </Button>
          <Button size="sm" onClick={onSubmit}>
            Next
          </Button>
        </DialogFooter>
      </div>
    </div>
  );
};

export default AuthorizedSignatoryDetails;
